import { useState, useCallback } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Search, Check, Users, Loader2, UserPlus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { getAvatarUrl } from '@/lib/avatars';
import { DraggableBottomSheet } from '@/components/ui/DraggableBottomSheet';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { triggerHaptic } from '@/hooks/useHaptics';
import { toast } from 'sonner';

interface Friend {
  id: string;
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
  handle?: string | null;
}

interface FriendPickerForPollProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedFriends: string[];
  onSelectionChange: (friends: string[]) => void;
}

export function FriendPickerForPoll({
  open,
  onOpenChange,
  selectedFriends,
  onSelectionChange,
}: FriendPickerForPollProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [followingInProgress, setFollowingInProgress] = useState<Set<string>>(new Set());

  // Fetch users the current user follows (friends)
  const { data: friends, isLoading: isLoadingFriends } = useQuery({
    queryKey: ['friends', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data: followData, error: followError } = await supabase
        .from('follows')
        .select('following_id')
        .eq('follower_id', user.id);
      
      if (followError) throw followError;
      if (!followData || followData.length === 0) return [];
      
      const followingIds = followData.map(f => f.following_id);
      
      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('id, user_id, display_name, avatar_url, handle')
        .in('user_id', followingIds);
      
      if (profileError) throw profileError;
      return profiles as Friend[];
    },
    enabled: !!user && open,
  });

  // Search all users when query is entered and user has no/few friends matching
  const shouldSearchAll = searchQuery.trim().length >= 2;
  const { data: searchResults, isLoading: isSearching } = useQuery({
    queryKey: ['user-search-poll', searchQuery.trim()],
    queryFn: async () => {
      if (!user) return [];
      const q = searchQuery.trim().toLowerCase();
      
      const { data, error } = await supabase
        .from('profiles')
        .select('id, user_id, display_name, avatar_url, handle')
        .neq('user_id', user.id)
        .or(`display_name.ilike.%${q}%,handle.ilike.%${q}%`)
        .limit(20);
      
      if (error) throw error;
      return data as Friend[];
    },
    enabled: !!user && open && shouldSearchAll,
    staleTime: 30_000,
  });

  const friendIds = new Set(friends?.map(f => f.user_id) || []);

  // Filter friends by search
  const filteredFriends = friends?.filter(friend =>
    !searchQuery.trim() ||
    friend.display_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    friend.handle?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  // Search results excluding already-followed users
  const newPeopleResults = searchResults?.filter(p => !friendIds.has(p.user_id)) || [];

  const toggleFriend = (userId: string) => {
    const newSelection = selectedFriends.includes(userId)
      ? selectedFriends.filter(id => id !== userId)
      : [...selectedFriends, userId];
    onSelectionChange(newSelection);
  };

  const handleFollow = useCallback(async (targetUserId: string) => {
    if (!user) return;
    setFollowingInProgress(prev => new Set(prev).add(targetUserId));
    triggerHaptic('medium');

    try {
      const { error } = await supabase
        .from('follows')
        .insert({ follower_id: user.id, following_id: targetUserId });
      
      if (error) throw error;

      // Refresh friends list so they appear in the main list
      await queryClient.invalidateQueries({ queryKey: ['friends', user.id] });
      toast.success('Followed!');
    } catch {
      toast.error('Could not follow');
    } finally {
      setFollowingInProgress(prev => {
        const next = new Set(prev);
        next.delete(targetUserId);
        return next;
      });
    }
  }, [user, queryClient]);

  const handleDone = () => onOpenChange(false);

  const hasFriends = (friends?.length || 0) > 0;
  const showDiscoverSection = shouldSearchAll && newPeopleResults.length > 0;
  const showEmptyHint = !hasFriends && !shouldSearchAll;

  return (
    <AnimatePresence>
      {open && (
        <DraggableBottomSheet
          open={open}
          onOpenChange={onOpenChange}
          title={
            <span className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Select Friends
            </span>
          }
          height="85vh"
        >
          <div className="flex flex-col h-full px-4 pb-4">
            {/* Selection count */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground">
                {selectedFriends.length > 0 
                  ? `${selectedFriends.length} friend${selectedFriends.length > 1 ? 's' : ''} selected`
                  : 'Select friends who can vote on your poll'
                }
              </p>
            </div>
            
            {/* Search */}
            <div className="relative mb-4 flex-shrink-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={hasFriends ? "Search friends..." : "Search people to follow..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-surface-2 border-0 rounded-xl"
              />
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto min-h-0 -mx-1 px-1" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div className="space-y-1 py-1">
                {isLoadingFriends ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <>
                    {/* Existing friends */}
                    {filteredFriends.length > 0 && (
                      <>
                        {showDiscoverSection && (
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-1 pt-1 pb-2">
                            Your Friends
                          </p>
                        )}
                        {filteredFriends.map(friend => (
                          <FriendRow
                            key={friend.user_id}
                            friend={friend}
                            isSelected={selectedFriends.includes(friend.user_id)}
                            onToggle={() => toggleFriend(friend.user_id)}
                          />
                        ))}
                      </>
                    )}

                    {/* Empty hint when no friends and no search */}
                    {showEmptyHint && filteredFriends.length === 0 && (
                      <div className="text-center py-8">
                        <UserPlus className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                        <p className="text-foreground font-medium">No friends yet</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Search for people above to follow them and add to your poll
                        </p>
                      </div>
                    )}

                    {/* Discover new people from search */}
                    {showDiscoverSection && (
                      <>
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-1 pt-4 pb-2">
                          People
                        </p>
                        {newPeopleResults.map(person => (
                          <DiscoverRow
                            key={person.user_id}
                            person={person}
                            isFollowing={followingInProgress.has(person.user_id)}
                            onFollow={() => handleFollow(person.user_id)}
                          />
                        ))}
                      </>
                    )}

                    {/* Searching indicator */}
                    {shouldSearchAll && isSearching && (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                      </div>
                    )}

                    {/* No results at all */}
                    {shouldSearchAll && !isSearching && filteredFriends.length === 0 && newPeopleResults.length === 0 && (
                      <div className="text-center py-8">
                        <p className="text-muted-foreground">No people found for "{searchQuery}"</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            
            {/* Done Button */}
            <div className="pt-4 flex-shrink-0">
              <Button
                onClick={handleDone}
                className="w-full h-12 rounded-full"
              >
                Done{selectedFriends.length > 0 && ` (${selectedFriends.length} selected)`}
              </Button>
            </div>
          </div>
        </DraggableBottomSheet>
      )}
    </AnimatePresence>
  );
}

/** Row for an existing friend (selectable) */
function FriendRow({ friend, isSelected, onToggle }: { friend: Friend; isSelected: boolean; onToggle: () => void }) {
  return (
    <button
      onClick={(e) => { e.preventDefault(); onToggle(); }}
      className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors ${
        isSelected
          ? 'bg-primary/10 ring-2 ring-primary ring-offset-2 ring-offset-background'
          : 'bg-surface-2 active:bg-surface-3'
      }`}
    >
      <Avatar className="w-10 h-10">
        <AvatarImage src={getAvatarUrl(friend.avatar_url, friend.id)} />
        <AvatarFallback className="bg-muted" />
      </Avatar>
      <div className="flex-1 text-left">
        <span className="font-medium text-foreground">{friend.display_name || 'Anonymous'}</span>
        {friend.handle && <p className="text-xs text-muted-foreground">@{friend.handle}</p>}
      </div>
      <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
        isSelected ? 'bg-primary text-primary-foreground' : 'bg-surface-3 text-muted-foreground'
      }`}>
        {isSelected && <Check className="w-4 h-4" />}
      </div>
    </button>
  );
}

/** Row for a discoverable user (followable) */
function DiscoverRow({ person, isFollowing, onFollow }: { person: Friend; isFollowing: boolean; onFollow: () => void }) {
  return (
    <div className="w-full flex items-center gap-3 p-3 rounded-xl bg-surface-2">
      <Avatar className="w-10 h-10">
        <AvatarImage src={getAvatarUrl(person.avatar_url, person.id)} />
        <AvatarFallback className="bg-muted" />
      </Avatar>
      <div className="flex-1 text-left min-w-0">
        <span className="font-medium text-foreground truncate block">{person.display_name || 'Anonymous'}</span>
        {person.handle && <p className="text-xs text-muted-foreground">@{person.handle}</p>}
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={onFollow}
        disabled={isFollowing}
        className="rounded-full text-xs h-8 px-4 gap-1.5"
      >
        {isFollowing ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <UserPlus className="w-3.5 h-3.5" />
        )}
        Follow
      </Button>
    </div>
  );
}
